import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import Step1Documents from './KYCSteps/Step1Documents';
import Step2Details from './KYCSteps/Step2Details';
import Step3Pricing from './KYCSteps/Step3Pricing';
import Step4Terms from './KYCSteps/Step4Terms';
import Step5Review from './KYCSteps/Step5Review';
import Step6IDVerification from './KYCSteps/Step6IDVerification';
import Step7Documents from './KYCSteps/Step7Documents';
import Step8Funding from './KYCSteps/Step8Funding';
import Step9Success from './KYCSteps/Step9Success';
const KYCModal = ({
   isOpen,
   onClose,
   currentStep,
   setCurrentStep,
   refreshStepsStatus,
   stepsStatus // <-- recibe stepsStatus
}) => {
   // Extraer userData además de userEmail del contexto UserContext
   const { userEmail, userData } = useUser();
   const [file1Selected, setFile1Selected] = useState(false);
   const [file2Selected, setFile2Selected] = useState(false);
   const [formData, setFormData] = useState({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      country: '',
      address: '',
      state: '',
      zipCode: ''
   });
   const [fundingForm, setFundingForm] = useState({
      bankName: '',
      fundingType: '',
      country: '',
      state: '',
      accountNumber: '',
      swiftCodeBic: '',
      bankAddress: '',
      zipCode: ''
   });
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [submitError, setSubmitError] = useState(null);
   const [termsAccepted, setTermsAccepted] = useState(false);
   const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result); // Devuelve el string completo
         reader.onerror = reject;
         reader.readAsDataURL(file);
      });
   };
   // Define el orden de los pasos igual que en kyc.js
   const steps = useMemo(() => [
      { key: 'yourDetails', label: 'Your Details' },
      { key: 'pricingFees', label: 'Pricing & Fees' },
      { key: 'termsConditions', label: 'Terms & Conditions' },
      { key: 'reviewVerify', label: 'Review & Verify' },
      { key: 'idVerification', label: 'ID Verification' },
      { key: 'uploadDocuments', label: 'Upload Documents' },
      { key: 'foundingDetails', label: 'Funding Details' }
   ], []); // Array de dependencias vacío, ya que esta definición nunca cambia
   // Efecto para saltar pasos completados
   useEffect(() => {
      if (!isOpen || !stepsStatus) return;
      // Si estamos en el paso informativo (case 1), no hacer nada
      if (currentStep === 1) return;
      // Busca el primer paso pendiente (después del informativo)
      const firstPendingIdx = steps.findIndex(step => !stepsStatus[step.key]);
      if (firstPendingIdx === -1) {
         // Todos completos, mostrar mensaje final
         if (currentStep !== 9) setCurrentStep(9);
         return;
      }
      // Si el paso actual ya está completado o no es el primer pendiente, salta al primer pendiente
      if (currentStep !== firstPendingIdx + 2) {
         setCurrentStep(firstPendingIdx + 2); // +2 porque case 1 es informativo, case 2 es el primer paso real
      }
   }, [isOpen, stepsStatus, currentStep, setCurrentStep, steps]); // Añadimos 'steps' al array de dependencias
   if (!isOpen) return null;
   const handleOverlayClick = (e) => {
      if (e.target.className === 'modal_overlay') {
         onClose();
      }
   };
   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };
   const handleFundingInputChange = (e) => {
      const { name, value } = e.target;
      setFundingForm(prev => ({
         ...prev,
         [name]: value
      }));
   };
   const handleSubmitStep1 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-1', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify(formData)
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to submit step 1 data');
         }
         setCurrentStep(3);
         if (typeof refreshStepsStatus === 'function') {
            refreshStepsStatus(); // <-- refresca el estado de los pasos
         }
      } catch (error) {
         console.error('Error submitting step 1:', error);
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep2 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-2', {
            method: 'GET', // Cambiado a GET
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            }
         });
         if (!response.ok) {
            if (response.status === 404) {
               throw new Error('El paso no está disponible. Contacta a soporte.');
            }
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to complete step 2');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['pricingFees']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['pricingFees']) {
            setCurrentStep(4);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep3 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-3', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify({ termsConditions: true })
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to submit step 3 data');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['termsConditions']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['termsConditions']) {
            setCurrentStep(5);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep4 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-4', {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            }
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to complete step 4');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['reviewVerify']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['reviewVerify']) {
            setCurrentStep(6);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep5 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const body = {
            documentType: "string",
            documentBase64: "string",
            selfieBase64: "string",
            identityDocument: "string",
            selfieOrVideoSelfie: "string",
            proofOfAddress: "string",
            fullName: "string",
            dateOfBirth: "string",
            nationality: "string",
            residentialAddress: "string",
            phoneNumber: "string",
            emailAddress: "string"
         };
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-5', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify(body)
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to complete step 5');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['idVerification']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['idVerification']) {
            setCurrentStep(7);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep6 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) throw new Error('No authentication token found');
         const file1 = document.getElementById('file1Input').files[0];
         const file2 = document.getElementById('file2Input').files[0];
         if (!file1 || !file2) throw new Error('Both documents are required');
         const [documentBankBase64, utilityBillBase64] = await Promise.all([
            fileToBase64(file1),
            fileToBase64(file2)
         ]);
         const body = { documentBankBase64, utilityBillBase64 };
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-6', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify(body)
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to complete step 6');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['uploadDocuments']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['uploadDocuments']) {
            setCurrentStep(8);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const handleSubmitStep7 = async () => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) throw new Error('No authentication token found');
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-7', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify(fundingForm)
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to complete step 7');
         }
         // Espera y verifica que el paso esté marcado como completado antes de avanzar
         let updatedStatus = {};
         for (let i = 0; i < 5; i++) {
            updatedStatus = await refreshStepsStatus();
            if (updatedStatus && updatedStatus['foundingDetails']) break;
            await new Promise(res => setTimeout(res, 300));
         }
         if (updatedStatus && updatedStatus['foundingDetails']) {
            setCurrentStep(9);
         } else {
            setSubmitError('No se pudo actualizar el estado del paso. Intenta de nuevo.');
         }
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsSubmitting(false);
      }
   };
   const renderStep = () => {
      switch (currentStep) {
         case 1:
            return <Step1Documents onClose={onClose} setCurrentStep={setCurrentStep} />;
         case 2:
            return (
               <Step2Details
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSubmitStep1={handleSubmitStep1}
                  isSubmitting={isSubmitting}
                  submitError={submitError}
                  setCurrentStep={setCurrentStep}
                  userData={userData} // Ahora userData está definido
                  userEmail={userEmail}
               />
            );
         case 3:
            return (
               <Step3Pricing
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep2={handleSubmitStep2}
                  isSubmitting={isSubmitting}
               />
            );
         case 4:
            return (
               <Step4Terms
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep3={handleSubmitStep3}
                  isSubmitting={isSubmitting}
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  submitError={submitError}
               />
            );
         case 5:
            return (
               <Step5Review
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep4={handleSubmitStep4}
                  isSubmitting={isSubmitting}
                  formData={formData}         // Añadido para poder mostrar los datos
                  handleInputChange={handleInputChange}  // Añadido para poder editar los datos
                  handleSubmitStep1={handleSubmitStep1}  // Añadido para enviar los cambios
               />
            );
         case 6:
            return (
               <Step6IDVerification
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep5={handleSubmitStep5}
                  isSubmitting={isSubmitting}
               />
            );
         case 7:
            return (
               <Step7Documents
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep6={handleSubmitStep6}
                  isSubmitting={isSubmitting}
                  file1Selected={file1Selected}
                  setFile1Selected={setFile1Selected}
                  file2Selected={file2Selected}
                  setFile2Selected={setFile2Selected}
                  submitError={submitError}
               />
            );
         case 8:
            return (
               <Step8Funding
                  setCurrentStep={setCurrentStep}
                  handleSubmitStep7={handleSubmitStep7}
                  isSubmitting={isSubmitting}
                  fundingForm={fundingForm}
                  handleFundingInputChange={handleFundingInputChange}
                  submitError={submitError}
               />
            );
         case 9:
            return <Step9Success />;
         default:
            return null;
      }
   };
   if (!isOpen) return null;
   return (
      <div className="modal_overlay" onClick={handleOverlayClick}>
         <div className="modal_content">
            <div className="modal_content_header">
               <span onClick={onClose} className="modal_content_header_close"></span>
            </div>
            {renderStep()}
         </div>
      </div>
   );
};
export default KYCModal;